import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
import { useAuthStore } from '@/stores/authStore';
import { Coins, Shield, BookOpen, Database, Menu, ChevronDown } from 'lucide-react';
import { ScpLogo } from './ScpLogo';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';

const navigation = [
  { name: '연재', href: '/novels' },
  { name: '도전', href: '/aspirant' },
];

const moreLinks = [
  { name: '랭킹', href: '/ranking' },
  { name: '공지사항', href: '/notices' },
  { name: '친구', href: '/friends' },
];

const featureLinks = [
  { name: 'SCP DB', href: '/scp', Icon: Database, color: 'text-indigo-600 dark:text-indigo-400' },
];

function MoreMenu({ items, isActive }: { items: Array<{ name: string; href: string }>; isActive: (href: string) => boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  // 외부 클릭 / Escape 닫기
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const focusItem = (idx: number) => {
    const len = items.length;
    const wrapped = ((idx % len) + len) % len;
    itemRefs.current[wrapped]?.focus();
  };

  // 트리거 키 핸들러: ↓ Enter Space로 열기 + 첫 항목 포커스
  const handleTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
      // 메뉴 렌더링 후 포커스
      setTimeout(() => focusItem(0), 0);
    }
  };

  // 항목 키 핸들러: ↑↓ Home End로 이동
  const handleItemKey = (e: React.KeyboardEvent<HTMLAnchorElement>, idx: number) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(idx + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(idx - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusItem(0); }
    else if (e.key === 'End') { e.preventDefault(); focusItem(items.length - 1); }
  };

  const anyActive = items.some(l => isActive(l.href));

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleTriggerKey}
        className={cn(
          'flex items-center gap-0.5 transition-colors hover:text-foreground/80',
          anyActive ? 'text-foreground font-semibold' : 'text-foreground/60',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        더보기
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full pt-2 z-50"
          role="menu"
        >
          <div className="min-w-[140px] rounded-md border bg-popover shadow-md py-1">
            {items.map((item, idx) => (
              <Link
                key={item.name}
                to={item.href}
                ref={(el) => { itemRefs.current[idx] = el; }}
                onClick={() => setOpen(false)}
                onKeyDown={(e) => handleItemKey(e, idx)}
                className={cn(
                  'block px-3 py-2 text-sm transition-colors hover:bg-accent focus:bg-accent focus:outline-none',
                  isActive(item.href) ? 'text-foreground font-semibold' : 'text-foreground/70',
                )}
                role="menuitem"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const location = useLocation();
  const { isAuthenticated, username, isAdmin, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdminUser = isAdmin();

  const isActive = (href: string) =>
    location.pathname === href || (href !== '/' && location.pathname.startsWith(href));

  const handleLogout = async () => {
    const { signOut } = await import('firebase/auth');
    const { auth } = await import('@/lib/firebase');
    await signOut(auth).catch(() => {});
    logout();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center gap-2">
        {/* 모바일: 햄버거 메뉴 */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger
            data-testid="header-mobile-trigger"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-md hover:bg-accent"
            aria-label="메뉴 열기"
          >
            <Menu className="h-6 w-6" />
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0">
            <SheetTitle className="sr-only">사이트 메뉴</SheetTitle>
            <div className="flex flex-col h-full">
              <div className="border-b p-4">
                <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                  <ScpLogo size={28} />
                  <span className="font-bold text-xl bg-gradient-to-r from-sky-400 to-indigo-500 bg-clip-text text-transparent">
                    SCP Novel
                  </span>
                </Link>
              </div>
              <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {[...navigation, ...moreLinks].map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      'block rounded-md px-3 py-3 text-base font-medium transition-colors',
                      isActive(item.href)
                        ? 'bg-accent text-foreground'
                        : 'text-foreground/70 hover:bg-accent/50',
                    )}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="pt-3 mt-3 border-t space-y-1">
                  {featureLinks.map(({ name, href, Icon, color }) => (
                    <Link
                      key={name}
                      to={href}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium transition-colors',
                        isActive(href) ? 'bg-accent' : 'hover:bg-accent/50',
                        color,
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {name}
                    </Link>
                  ))}
                </div>
              </nav>
              {isAuthenticated && (
                <div className="border-t p-4 space-y-2">
                  <p className="text-sm text-muted-foreground px-2">{username}님</p>
                  <Link
                    to="/author"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent/50"
                  >
                    <BookOpen className="h-4 w-4" /> 작가
                  </Link>
                  <Link
                    to="/payment"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent/50"
                  >
                    <Coins className="h-4 w-4" /> 충전
                  </Link>
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-md px-3 py-2 text-sm hover:bg-accent/50"
                  >
                    계정 설정
                  </Link>
                  {isAdminUser && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent/50"
                    >
                      <Shield className="h-4 w-4" /> 관리자
                    </Link>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                  >
                    로그아웃
                  </Button>
                </div>
              )}
              {!isAuthenticated && (
                <div className="border-t p-4 space-y-2">
                  <Button asChild variant="ghost" className="w-full">
                    <Link to="/login" onClick={() => setMobileOpen(false)}>로그인</Link>
                  </Button>
                  <Button asChild className="w-full">
                    <Link to="/signup" onClick={() => setMobileOpen(false)}>회원가입</Link>
                  </Button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* 로고 */}
        <Link to="/" className="flex items-center gap-2 mr-2">
          <ScpLogo size={28} />
          <span className="font-bold text-lg sm:text-xl bg-gradient-to-r from-sky-400 to-indigo-500 bg-clip-text text-transparent">
            SCP Novel
          </span>
        </Link>

        {/* 데스크톱 네비게이션 */}
        <nav
          data-testid="header-desktop-nav"
          className="hidden md:flex items-center space-x-5 text-sm font-medium ml-4"
        >
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'transition-colors hover:text-foreground/80',
                isActive(item.href) ? 'text-foreground font-semibold' : 'text-foreground/60',
              )}
            >
              {item.name}
            </Link>
          ))}

          {/* 더보기 드롭다운 (랭킹/친구) — hover/focus/click 모두 지원 */}
          <MoreMenu items={moreLinks} isActive={isActive} />

          {featureLinks.map(({ name, href, Icon }) => (
            <Link
              key={name}
              to={href}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                isActive(href)
                  ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/25'
                  : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20 dark:text-indigo-400',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {name}
            </Link>
          ))}
        </nav>

        {/* 우측 액션 */}
        <div className="flex flex-1 items-center justify-end gap-1 sm:gap-2">
          <ThemeToggle />
          {/* 데스크톱 전용 액션 (모바일은 햄버거에서 처리) */}
          <div className="hidden md:flex items-center gap-2">
            {isAuthenticated && (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/author" className="flex items-center gap-1">
                  <BookOpen className="h-4 w-4" />
                  작가
                </Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" asChild>
              <Link to="/payment" className="flex items-center gap-1">
                <Coins className="h-4 w-4" />
                충전
              </Link>
            </Button>
            {isAuthenticated && isAdminUser && (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin" className="flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  관리자
                </Link>
              </Button>
            )}
            {isAuthenticated ? (
              <>
                <Link to="/account" className="text-sm text-muted-foreground mr-2 hover:text-foreground hover:underline">{username}님</Link>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  로그아웃
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/login">로그인</Link>
                </Button>
                <Button variant="default" size="sm" asChild>
                  <Link to="/signup">회원가입</Link>
                </Button>
              </>
            )}
          </div>
          {/* 모바일 전용: 미인증 시 로그인 버튼만 우측에 표시 */}
          {!isAuthenticated && (
            <div className="md:hidden">
              <Button variant="default" size="sm" asChild>
                <Link to="/login">로그인</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
